import { useState, useRef, useCallback } from 'react';
import { toast } from 'react-toastify';

/**
 * useBlogChat
 *
 * Streaming chat hook for the AI blog authoring assistant.
 * - POSTs to /api/admin/blog-chat
 * - Consumes SSE via fetch + ReadableStream (supports POST + auth headers)
 * - Every turn is routed through the backend planner (no client-side "stage" bypass).
 * - Emits events: plan (routing decision), agent_start, delta (text), patch (structured
 *   UI update: target "metadata" | "research"), usage, done, error
 *
 * Returns:
 *   messages: [{role, content, timestamp?}, ...]
 *   isStreaming, error, usage (last)
 *   plan: last PlannerResult.intents array (for showing "routed to: X" UI)
 *   activeAgent: {agent, label} | null — the agent currently executing
 *   sendMessage(text, options), stop(), retry(), reset()
 *
 * options.onPatch(target, data) is invoked synchronously whenever a "patch" SSE event
 * arrives, so the parent component can apply structured UI updates (metadata/research)
 * immediately and reliably, without regex-parsing chat text.
 */
export function useBlogChat(initialMessages = [], { onPatch } = {}) {
  const [messages, setMessages] = useState(initialMessages);
  const [isStreaming, setIsStreaming] = useState(false);
  const [error, setError] = useState(null);
  const [lastUsage, setLastUsage] = useState(null);
  const [plan, setPlan] = useState([]);
  const [activeAgent, setActiveAgent] = useState(null);

  const abortRef = useRef(null);
  const pendingAssistantRef = useRef(null); // index of the assistant message being streamed
  const onPatchRef = useRef(onPatch);
  onPatchRef.current = onPatch;

  const reset = useCallback(() => {
    setMessages([]);
    setIsStreaming(false);
    setError(null);
    setLastUsage(null);
    setPlan([]);
    setActiveAgent(null);
    abortRef.current?.abort();
    abortRef.current = null;
    pendingAssistantRef.current = null;
  }, []);

  const stop = useCallback(() => {
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setIsStreaming(false);
  }, []);

  // appendMessage helper not needed (we manage indices via state directly in the stream loop)

  const updateLastAssistant = useCallback((updater) => {
    setMessages((prev) => {
      if (prev.length === 0) return prev;
      const lastIdx = prev.length - 1;
      const last = prev[lastIdx];
      if (last.role !== 'assistant') return prev;
      const next = { ...last };
      updater(next);
      const copy = [...prev];
      copy[lastIdx] = next;
      return copy;
    });
  }, []);

  const sendMessage = useCallback(async (text, options = {}) => {
    const userMessage = {
      role: 'user',
      content: text,
      timestamp: new Date().toISOString(),
    };

    // Append user message immediately
    setMessages((prev) => [...prev, userMessage]);

    // Prepare assistant placeholder
    const assistantMessage = {
      role: 'assistant',
      content: '',
      timestamp: new Date().toISOString(),
    };

    setMessages((prev) => [...prev, assistantMessage]);
    const assistantIndex = messages.length + 1; // after user append above in the snapshot

    // We will use a ref to track the real index because state is async
    pendingAssistantRef.current = assistantIndex;

    setIsStreaming(true);
    setError(null);
    setPlan([]);
    setActiveAgent(null);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      // We intentionally skip using axios for the actual stream.
      // The fetch below (with auth header) is the real streaming path.

    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') {
        // user stopped
        setIsStreaming(false);
        return;
      }
      setError(err.message || 'Failed to start chat');
      toast.error('Failed to contact AI');
      setIsStreaming(false);
      // remove the empty assistant placeholder
      setMessages((prev) => prev.slice(0, -1));
      return;
    }

    // --- Proper fetch-based SSE consumption ---
    // Rebuild the exact payload, but NEVER send full article drafts in the history.
    // Any long assistant message (or one containing the chat-summary marker) is collapsed
    // to its short summary for the wire. This prevents 413 on /api/admin/blog-chat and
    // keeps the backend payload tiny. The full draft lives in proposedDraft on the client.
    const CHAT_SUMMARY_START = '---CHAT-SUMMARY---';
    const CHAT_SUMMARY_END = '---END-CHAT-SUMMARY---';

    const currentMessages = [...messages, userMessage];
    const wireMessages = currentMessages.map((m) => {
      if (m.role !== 'assistant' || typeof m.content !== 'string') {
        return { role: m.role, content: m.content };
      }
      const c = m.content;
      if (c.includes(CHAT_SUMMARY_START) || c.length > 700) {
        const start = c.indexOf(CHAT_SUMMARY_START);
        if (start !== -1) {
          let sum = c.slice(start);
          const end = sum.indexOf(CHAT_SUMMARY_END);
          if (end !== -1) sum = sum.slice(0, end + CHAT_SUMMARY_END.length);
          const summaryText = sum
            .replace(CHAT_SUMMARY_START, '')
            .replace(CHAT_SUMMARY_END, '')
            .trim();
          return { role: m.role, content: summaryText || c.slice(0, 350) + '…' };
        }
        // No marker but long: send a short prefix + note so backend knows to look at context
        return { role: m.role, content: c.slice(0, 350) + '… [full draft in editor / Differences tab]' };
      }
      return { role: m.role, content: m.content };
    });

    // No client-side "stage" bypass: every turn (free text or a button's predefined prompt)
    // is classified by the backend planner. `context` grounds the planner/agents
    // (topic, outline, content, title, slug, excerpt, tags, instructions).
    const payload = {
      messages: wireMessages,
      context: options.context || null,
    };

    const token = localStorage.getItem('cts_admin_token');

    try {
      const res = await fetch('/api/admin/blog-chat', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
          Accept: 'text/event-stream',
        },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });

      if (!res.ok || !res.body) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `HTTP ${res.status}`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE frames (split on \n\n)
        let idx;
        while ((idx = buffer.indexOf('\n\n')) !== -1) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);

          // frame may contain multiple "data: ..." lines, but our backend emits one per event
          const lines = frame.split('\n').map((l) => l.trim()).filter(Boolean);
          for (const line of lines) {
            if (!line.startsWith('data:')) continue;
            const jsonStr = line.slice(5).trim();
            if (!jsonStr) continue;

            let event;
            try {
              event = JSON.parse(jsonStr);
            } catch {
              // partial or malformed; ignore
              continue;
            }

            if (event.type === 'plan') {
              setPlan(event.intents || []);
            } else if (event.type === 'agent_start') {
              setActiveAgent({ agent: event.agent, label: event.label || '' });
            } else if (event.type === 'delta') {
              updateLastAssistant((m) => {
                m.content = (m.content || '') + (event.text || '');
              });
            } else if (event.type === 'patch') {
              // Structured, deterministic UI update (target: "metadata" | "research").
              // Applied immediately by the parent via onPatch — no regex-parsing needed.
              if (onPatchRef.current) {
                onPatchRef.current(event.target, event.data || {});
              }
            } else if (event.type === 'usage') {
              setLastUsage({
                input_tokens: event.input_tokens,
                output_tokens: event.output_tokens,
              });
            } else if (event.type === 'done') {
              setIsStreaming(false);
              abortRef.current = null;
            } else if (event.type === 'error') {
              setIsStreaming(false);
              abortRef.current = null;
              const msg = event.message || event.error || 'AI error';
              setError(msg);
              toast.error(msg);
            }
          }
        }
      }

      // Final flush
      if (buffer.trim()) {
        // try last frame
        const lines = buffer.split('\n').map((l) => l.trim()).filter(Boolean);
        for (const line of lines) {
          if (line.startsWith('data:')) {
            try {
              const ev = JSON.parse(line.slice(5).trim());
              if (ev.type === 'delta') {
                updateLastAssistant((m) => { m.content = (m.content || '') + (ev.text || ''); });
              }
            } catch {}
          }
        }
      }

      setIsStreaming(false);
      abortRef.current = null;

    } catch (err) {
      if (err.name === 'AbortError') {
        // stopped by user — keep whatever we have
        setIsStreaming(false);
        abortRef.current = null;
        return;
      }
      setIsStreaming(false);
      abortRef.current = null;
      setError(err.message || 'Stream error');
      toast.error('AI stream failed');
      // Do not remove partial content; user can retry or edit
    }
  }, [messages, updateLastAssistant]);

  const retry = useCallback(() => {
    // Find the last user message and resend it
    const lastUser = [...messages].reverse().find((m) => m.role === 'user');
    if (!lastUser) return;
    // Remove the trailing assistant (failed) message if present
    setMessages((prev) => {
      if (prev.length && prev[prev.length - 1].role === 'assistant') {
        return prev.slice(0, -1);
      }
      return prev;
    });
    // Re-send the user content
    return sendMessage(lastUser.content);
  }, [messages, sendMessage]);

  return {
    messages,
    isStreaming,
    error,
    lastUsage,
    plan,
    activeAgent,
    sendMessage,
    stop,
    retry,
    reset,
    setMessages, // allow parent to hydrate from persisted session
  };
}
